import { objectEntries } from "../object";
import {
  _2dMapCombinedQuote,
  _2dMapQuote,
  CustomOrderQuoteItem,
} from "../type/order.v2";

export function redistributeOverriddenItems(quote: _2dMapCombinedQuote): {
  buyNowQuote: _2dMapQuote;
  getQuoteQuote: _2dMapQuote;
  buyNowCustomOrders: CustomOrderQuoteItem[];
  getQuoteCustomOrders: CustomOrderQuoteItem[];
} {
  const customOrders = quote.customOrders ?? [];
  const overriddenProducts = quote.overriddenProducts ?? [];

  if (overriddenProducts.length === 0) {
    return {
      buyNowQuote: quote.nonRfq,
      getQuoteQuote: quote.rfq,
      buyNowCustomOrders: [],
      getQuoteCustomOrders: customOrders,
    };
  }

  const overriddenSet = new Set(overriddenProducts);
  const isFeeOverridden = overriddenSet.has("site_deployment_fee");

  const buyNowComposition: _2dMapQuote["composition"] = {
    ...quote.nonRfq.composition,
  };
  const getQuoteComposition: _2dMapQuote["composition"] = {};

  for (const [groupKey, subQuote] of objectEntries(quote.rfq.composition)) {
    if (!subQuote) continue;

    const overriddenCharges = subQuote.charges.filter((c) =>
      overriddenSet.has(c.product)
    );
    const remainingCharges = subQuote.charges.filter(
      (c) => !overriddenSet.has(c.product)
    );

    // site_deployment_fee lives on rfq capture_services as the first fee.
    const moveFee = groupKey === "capture_services" && isFeeOverridden;
    const overriddenFees = moveFee ? subQuote.totals.fees.slice(0, 1) : [];
    const remainingFees = moveFee
      ? subQuote.totals.fees.slice(1)
      : subQuote.totals.fees;

    if (overriddenCharges.length > 0 || overriddenFees.length > 0) {
      const existing = buyNowComposition[groupKey];
      buyNowComposition[groupKey] = {
        charges: [...(existing?.charges ?? []), ...overriddenCharges],
        totals: {
          ...(existing?.totals ?? subQuote.totals),
          fees: [...(existing?.totals.fees ?? []), ...overriddenFees],
        },
      };
    }

    if (remainingCharges.length > 0 || remainingFees.length > 0) {
      getQuoteComposition[groupKey] = {
        charges: remainingCharges,
        totals: { ...subQuote.totals, fees: remainingFees },
      };
    }
  }

  const buyNowCustomOrders = customOrders.filter((o) =>
    overriddenSet.has(`custom_${o.id}`)
  );
  const getQuoteCustomOrders = customOrders.filter(
    (o) => !overriddenSet.has(`custom_${o.id}`)
  );

  return {
    buyNowQuote: {
      composition: buyNowComposition,
      totals: quote.buyNowTotals ?? quote.nonRfq.totals,
      currency: quote.nonRfq.currency,
    },
    getQuoteQuote: {
      composition: getQuoteComposition,
      totals: quote.getQuoteTotals ?? quote.rfq.totals,
      currency: quote.rfq.currency,
    },
    buyNowCustomOrders,
    getQuoteCustomOrders,
  };
}
